import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryState } from './QueryState';
import {
  listMembers,
  listInvitations,
  inviteToProject,
  revokeInvitation,
  removeMember,
  setMemberRole,
  ApiError,
} from '../api';

/**
 * Member management on the project view (Phase 8).
 *
 * Who sees the controls follows the ladder rather than ownership. This screen was written when
 * only the owner could share a project; the roles work made `admin` the level for adding and
 * removing members, so a maintainer could do all of it over the API and saw none of it here.
 * Granting *ownership* is still its own act at its own level, which is why `owner` is not on the
 * list of roles anyone is offered.
 *
 * The server enforces every one of these regardless — this is a matter of not showing buttons
 * that would be refused, not a security boundary.
 */

/*
  Said in the words the person choosing has to act on, not in the words the ladder is written in.

  `roleAllows` is the canonical statement of what each role may do; this is the same four rows
  turned around to face whoever is filling in the invite.
*/
const ROLE_HELP: Record<string, string> = {
  reporter: 'Sees and discusses everything. Changes nothing.',
  developer: 'Takes on, releases and assigns work, and links tasks that depend on each other.',
  maintainer: 'Creates and edits tasks, manages labels, and shares the project.',
  owner: 'Everything, including deleting the project.',
};

/** "in 12 days" / "6 days ago" — the invitation's own clock, in the reader's words. */
function expiryText(iso: string): string {
  const days = Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days > 1) return `in ${days} days`;
  if (days === 1) return 'tomorrow';
  if (days === 0) return 'today';
  if (days === -1) return 'yesterday';
  return `${Math.abs(days)} days ago`;
}

/* Owner is not offered: granting ownership is its own act, at its own level. */
const INVITABLE = ['reporter', 'developer', 'maintainer'] as const;

export function Members({
  projectId,
  canAdminister,
  myUserId,
  onLeft,
}: {
  projectId: string;
  /** Owner or maintainer — the level the server requires to add or remove anybody. */
  canAdminister: boolean;
  myUserId: string | undefined;
  /** Leaving closes the project: it is no longer reachable, so keeping it open would 404. */
  onLeft: () => void;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('reporter');
  const [lastLink, setLastLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => listMembers(projectId),
  });
  const invitations = useQuery({
    queryKey: ['invitations', projectId],
    queryFn: () => listInvitations(projectId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['members', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['invitations', projectId] });
  };

  const invite = useMutation({
    mutationFn: () => inviteToProject(projectId, { email: email.trim(), role }),
    onSuccess: (result) => {
      setEmail('');
      setError(null);
      // Shown once: only the hash is stored, so this is the only chance to copy it.
      setLastLink(result.link);
      refresh();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not send that invitation.'),
  });

  const withdraw = useMutation({
    mutationFn: (id: string) => revokeInvitation(projectId, id),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (userId: string) => removeMember(projectId, userId),
    onSuccess: (_result, userId) => {
      if (userId === myUserId) onLeft();
      else refresh();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not remove them.'),
  });
  const changeRole = useMutation({
    mutationFn: ({ userId, next }: { userId: string; next: string }) =>
      setMemberRole(projectId, userId, next),
    onSuccess: refresh,
  });

  return (
    <section className="card">
      <h3>People</h3>

      <QueryState query={members} />
      <ul className="plain">
        {members.data?.map((m) => (
          <li key={m.userId} className="row between member-row">
            <span>
              <strong>{m.displayName || m.email}</strong>
              <span className="subtext">{m.email}</span>
            </span>

            <span className="row gap">
              {/*
                A row that never joined gets the pill, not the picker.

                `POST /members` sets the role of somebody who is *in* the project and refuses
                anybody who is not — a member is an accepted one everywhere else in the app, and
                the rows below this line with no `acceptedAt` are what the old add-a-member route
                left behind. Offering a dropdown that can only 404 is worse than saying plainly
                that there is nothing to set yet: they are invited back through "Invite someone".
              */}
              {canAdminister &&
              m.role !== 'owner' &&
              m.userId !== myUserId &&
              m.acceptedAt !== null ? (
                /*
                  The same caption as the invite picker, on the picker reached *later*.

                  It was wired only to "Invite someone" — the control used once per person — and
                  not to the dropdown an owner reaches for when reconsidering a role, which is
                  exactly when the reminder matters. "manages labels" is the one sentence in the
                  app that says who may change a shared taxonomy, and this is where somebody
                  deciding to promote a member for that reason is looking.
                */
                <span className="role-choice">
                  <select
                    value={m.role}
                    aria-label={`Role for ${m.email}`}
                    title={ROLE_HELP[m.role]}
                    onChange={(e) => changeRole.mutate({ userId: m.userId, next: e.target.value })}
                  >
                    {INVITABLE.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  <span className="muted small" data-testid="role-caption">
                    {ROLE_HELP[m.role]}
                  </span>
                </span>
              ) : (
                <span className="pill" title={ROLE_HELP[m.role]}>
                  {m.role}
                </span>
              )}

              {m.acceptedAt === null && <span className="pill warn">not yet joined</span>}

              {/*
                Leaving is not removal, and it is the one control every member has. Requiring
                admin to remove yourself is what made §7.4's "leaves or is removed" have no
                "leaves" in it — so the same row offers *Leave* to you and *Remove* to an
                administrator looking at somebody else.
              */}
              {m.userId === myUserId && m.role !== 'owner' && (
                <button className="link danger" onClick={() => remove.mutate(m.userId)}>
                  Leave
                </button>
              )}
              {canAdminister && m.role !== 'owner' && m.userId !== myUserId && (
                <button className="link danger" onClick={() => remove.mutate(m.userId)}>
                  Remove
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>

      {invitations.data && invitations.data.length > 0 && (
        <>
          <h4>Invited</h4>
          <ul className="plain">
            {invitations.data.map((i) => (
              <li key={i.id} className="row between">
                <span>
                  {i.email} <span className="muted small">as {i.role}</span>
                </span>
                <span className="row gap">
                  {/*
                    Which of these two an offer is was the question the screen could not answer.
                    Three invitations sent, nothing heard, and every one of them rendered
                    identically whether it was still live or had run out a fortnight ago — so
                    "waiting" and "dead" looked the same, and the only way to tell was to sign
                    into the other person's account.

                    It says nothing about delivery, and cannot: there is no channel here whose
                    delivery could be observed, and a "delivered" state would tell the inviter
                    whether the address has an account on this instance.
                  */}
                  {i.status === 'expired' ? (
                    <span className="pill warn" title={`Ran out ${expiryText(i.expiresAt)}`}>
                      expired
                    </span>
                  ) : (
                    <span className="pill" title={`Expires ${expiryText(i.expiresAt)}`}>
                      awaiting reply
                    </span>
                  )}
                  {canAdminister && (
                    <button className="link danger" onClick={() => withdraw.mutate(i.id)}>
                      {i.status === 'expired' ? 'Remove' : 'Withdraw'}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {canAdminister && (
        <form
          className="invite-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim()) invite.mutate();
          }}
        >
          <h4>Invite someone</h4>
          <div className="row gap">
            <input
              type="email"
              value={email}
              placeholder="their@email.test"
              onChange={(e) => setEmail(e.target.value)}
              aria-label="Email to invite"
            />
            <select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Role">
              {INVITABLE.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <button type="submit" disabled={invite.isPending}>
              Invite
            </button>
          </div>
          <p className="muted small">{ROLE_HELP[role]}</p>

          {error && <p className="error">{error}</p>}

          {lastLink && (
            <div className="notice">
              <strong>Invitation created</strong>
              {/*
                Said conditionally, and that is the point rather than vagueness.

                If it already has an account here the offer is sitting in that account's
                notifications; if it does not, this link is the only way it will ever arrive,
                because this deployment has no mail. Which of the two is true is exactly what
                the inviter must not be told — `listMembers` masks a pending invitee's identity
                so that inviting a guessed address cannot confirm who is registered here, and a
                notice that said "we told them" would give that back on the next screen.
              */}
              <p className="muted small">
                If they already have an account here, it is waiting in their notifications.
                Otherwise send them this link — it is shown once, and only the address above can use
                it.
              </p>
              <code className="invite-link">{window.location.origin + lastLink}</code>
            </div>
          )}
        </form>
      )}
    </section>
  );
}
